import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { formatCurrencyFromString, useDebouncedValue } from '@/shared/utils';
import { getFormattedCurrency } from '@/shared/utils/currencyFormatter';
import { Field, Select, Text } from '@noorden-insurtech/alba-design-system';
import { SquarePen } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  getNormalFlowCoverageAnalysisWarning,
  getPercentCapValue,
  isDeductibleOptionValidForLmi,
} from '../utils/business-location-coverage-validation.util';
import { getDeductibleOptionLabelWithDiscount } from '../utils/deductible-discount.util';
import { resolveSingleDeductibleOption } from '../utils/single-deductible-option.util';
import type { DeductibleOption } from '../utils/map-deductible-options.util';
import {
  CustomizeDeductibleDialog,
  formatCustomDeductibleLabel,
  type CustomDeductible,
} from './customize-deductible.dialog';

export type { CustomDeductible } from './customize-deductible.dialog';
export { formatCustomDeductibleLabel } from './customize-deductible.dialog';
export type { DeductibleOption } from '../utils/map-deductible-options.util';
export { mapDeductibleOptions } from '../utils/map-deductible-options.util';

export type CoverageCardData = {
  id: string;
  code?: string;
  productElementId?: string;
  title: string;
  description: string;
  required?: boolean;
  limit: { type: 'range'; constraints: { min: number; max: number } };
  limitValue: string;
  deductible: string;
  deductibleOptions?: DeductibleOption[];
  indemnityPeriod?: string;
  customDeductible?: CustomDeductible;
  /**
   * Exibe o botão "Personalizar franquia" (edição manual de percentual/valor
   * via CustomizeDeductibleDialog). Opt-in por produto — hoje habilitado
   * apenas no empresarial, pelos modais de local de risco.
   */
  allowCustomDeductible?: boolean;
  onLimitChange?: (value: string) => void;
  onDeductibleChange?: (value: string) => void;
  onIndemnityPeriodChange?: (value: string) => void;
  onCustomDeductibleChange?: (value: CustomDeductible | undefined) => void;
  showValidationErrors?: boolean;
  disabled?: boolean;
  validationError?: string;
  /** Jornada de análise: permite valores e exibe aviso do fluxo normal. */
  skipValueValidation?: boolean;
  /** Valor em risco do local — usado para avisos na jornada de análise. */
  riskValueLimit?: number;
  /**
   * Teto percentual do limite (`limit.percent` do catálogo) sobre o LMI da
   * cobertura básica. Não se aplica à própria cobertura básica.
   */
  percentOfBasicLimit?: number | null;
  /** LMI atual da cobertura básica do local, base do teto percentual. */
  basicCoverageLmi?: number;
};

type CoverageCardProps = {
  coverage: CoverageCardData;
};

export function CoverageCard({ coverage }: CoverageCardProps) {
  const isRequired = coverage.required;
  const min = coverage.limit.constraints.min ?? 0;
  const max = coverage.limit.constraints.max ?? Number.MAX_SAFE_INTEGER;
  const showValidationErrors = coverage.showValidationErrors ?? false;
  const isDisabled = coverage.disabled ?? false;
  const skipValueValidation = coverage.skipValueValidation ?? false;
  const hasBackendError =
    !skipValueValidation && !!coverage.validationError;
  const deductibleOptions = coverage.deductibleOptions ?? [];
  const hasDeductibleOptions = deductibleOptions.length > 0;

  const parseCurrencyToNumber = (raw: string) => {
    const numeric = parseInt(String(raw || '').replace(/\D/g, ''), 10);
    return Number.isNaN(numeric) ? 0 : numeric / 100;
  };

  const limitValueNumber = parseCurrencyToNumber(coverage.limitValue);

  // Debounce de 1s no valor da cobertura: o seletor de franquia só é habilitado/
  // filtrado após o usuário parar de digitar o LMI.
  const debouncedLimitValue = useDebouncedValue(coverage.limitValue ?? '', 1000);
  const debouncedLimitValueNumber = parseCurrencyToNumber(debouncedLimitValue);

  const isLmiFilled = debouncedLimitValueNumber > 0;

  // Oculta a franquia quando o LMI está fora da faixa [minSi, maxSi] — ou,
  // quando a faixa explícita falta, fora das faixas de desconto (mesma regra
  // da validação de submit, em isDeductibleOptionValidForLmi).
  const isDeductibleVisible = (option: DeductibleOption) =>
    isDeductibleOptionValidForLmi(option, debouncedLimitValueNumber);

  // A filtragem por faixa de SI só faz sentido após o LMI ser preenchido.
  const visibleDeductibleOptions =
    skipValueValidation || !isLmiFilled
      ? deductibleOptions
      : deductibleOptions.filter(isDeductibleVisible);

  // O desconto da franquia varia por faixa de SI ([siMin, siMax]): com o LMI
  // preenchido, anexa "x% de desconto" ao label da opção quando há faixa
  // aplicável ao valor informado.
  const enrichedDeductibleOptions = visibleDeductibleOptions.map(option =>
    isLmiFilled
      ? {
          ...option,
          label: getDeductibleOptionLabelWithDiscount(
            option.label,
            option.discount,
            debouncedLimitValueNumber
          ),
        }
      : option
  );

  // Franquias de código repetido (grupos de atividade) só fazem sentido em
  // dobro quando o desconto efetivo para o LMI atual difere — quando o label
  // final fica idêntico, colapsa em uma opção só, preservando a selecionada.
  const displayDeductibleOptions = (() => {
    const indexByKey = new Map<string, number>();
    const result: DeductibleOption[] = [];
    for (const option of enrichedDeductibleOptions) {
      const key = `${option.code}::${option.label}`;
      const existingIndex = indexByKey.get(key);
      if (existingIndex === undefined) {
        indexByKey.set(key, result.length);
        result.push(option);
      } else if (option.value === coverage.deductible) {
        result[existingIndex] = option;
      }
    }
    return result;
  })();

  // O seletor de franquia permanece desabilitado até o LMI da cobertura ser informado.
  const isDeductibleDisabled =
    isDisabled || (!skipValueValidation && !isLmiFilled);

  // LMI informado, mas nenhuma franquia da cobertura aceita esse valor (todas
  // ficaram fora da faixa [minSi, maxSi]): o seletor ficaria vazio sem explicação.
  const hasNoAvailableDeductibles =
    !skipValueValidation &&
    hasDeductibleOptions &&
    isLmiFilled &&
    visibleDeductibleOptions.length === 0;

  const isFireCoverage = /inc[eê]ndio/i.test(coverage.title);
  const belowMin = limitValueNumber > 0 && limitValueNumber < min;
  const aboveMax = limitValueNumber > max;
  // Teto percentual sobre o LMI da cobertura básica (limit.percent) — não se
  // aplica à própria cobertura básica (obrigatória).
  const percentCap = isRequired
    ? undefined
    : getPercentCapValue(
        coverage.percentOfBasicLimit,
        coverage.basicCoverageLmi
      );
  const abovePercentCap =
    !skipValueValidation &&
    percentCap !== undefined &&
    limitValueNumber > percentCap;
  const isLimitOutOfRange =
    !skipValueValidation && !isFireCoverage && (belowMin || aboveMax);
  const isLimitRequiredEmpty =
    !skipValueValidation &&
    isRequired &&
    showValidationErrors &&
    limitValueNumber === 0;
  const isLimitInvalid =
    isLimitOutOfRange || isLimitRequiredEmpty || abovePercentCap;
  const fireMaxExceeded =
    !skipValueValidation && isFireCoverage && aboveMax;

  // O erro de limite (máximo/percentual) tem prioridade sobre as mensagens de
  // franquia: só reclama de franquia quando o LMI está dentro dos limites.
  const showNoAvailableDeductibles =
    hasNoAvailableDeductibles &&
    !isLimitOutOfRange &&
    !abovePercentCap &&
    !fireMaxExceeded;

  const hasDeductible =
    typeof coverage.deductible === 'string' &&
    coverage.deductible.trim().length > 0;
  const isDeductibleInvalid =
    !skipValueValidation &&
    showValidationErrors &&
    isRequired &&
    hasDeductibleOptions &&
    !hasDeductible;

  const selectedDeductibleOption = deductibleOptions.find(
    option => option.value === coverage.deductible
  );

  const normalFlowWarning = useMemo(() => {
    if (!skipValueValidation) return undefined;

    return getNormalFlowCoverageAnalysisWarning({
      name: coverage.title,
      required: isRequired,
      lmiValue: debouncedLimitValueNumber,
      riskValueLimit: coverage.riskValueLimit ?? 0,
      minLimit: min,
      maxLimit: max,
      percentOfBasicLimit: isRequired
        ? undefined
        : coverage.percentOfBasicLimit,
      basicCoverageLmi: coverage.basicCoverageLmi,
      isFireCoverage,
      hasDeductibleOptions,
      hasDeductibleSelected: hasDeductible,
      isSelectedDeductibleValidForLmi: selectedDeductibleOption
        ? isDeductibleOptionValidForLmi(
            selectedDeductibleOption,
            debouncedLimitValueNumber
          )
        : true,
    });
  }, [
    skipValueValidation,
    coverage.title,
    coverage.riskValueLimit,
    coverage.percentOfBasicLimit,
    coverage.basicCoverageLmi,
    isRequired,
    debouncedLimitValueNumber,
    min,
    max,
    isFireCoverage,
    hasDeductibleOptions,
    hasDeductible,
    selectedDeductibleOption,
  ]);

  const [isCustomizeDialogOpen, setIsCustomizeDialogOpen] = useState(false);
  const customDeductible = coverage.customDeductible;
  const hasCustomDeductible =
    !!customDeductible &&
    (customDeductible.percentage?.trim().length ?? 0) > 0 &&
    (customDeductible.value?.trim().length ?? 0) > 0;
  const customDeductibleLabel = hasCustomDeductible
    ? formatCustomDeductibleLabel(customDeductible!)
    : '';
  const selectedDeductibleLabel =
    deductibleOptions.find(opt => opt.value === coverage.deductible)?.label ?? '';
  const dialogDefaultLabel =
    selectedDeductibleLabel || deductibleOptions[0]?.label || '';

  const handleApplyCustomDeductible = (value: CustomDeductible) => {
    coverage.onCustomDeductibleChange?.(value);
  };

  const handleDeductibleSelect = (value: string) => {
    coverage.onDeductibleChange?.(value);
    if (hasCustomDeductible) {
      coverage.onCustomDeductibleChange?.(undefined);
    }
  };

  // Ao alterar o LMI da cobertura, revalida a franquia já selecionada: se o novo
  // valor ficar fora da faixa [minSi, maxSi] da franquia (ou o LMI for esvaziado),
  // a seleção é desfeita — junto com a franquia personalizada, que é derivada
  // da seleção base.
  const clearDeductibleSelection = () => {
    coverage.onDeductibleChange?.('');
    if (hasCustomDeductible) {
      coverage.onCustomDeductibleChange?.(undefined);
    }
  };

  useEffect(() => {
    if (skipValueValidation) return;
    if (!coverage.deductible) return;
    // LMI vazio (valor cru, sem debounce): o seletor volta a ficar
    // desabilitado, então limpa a franquia.
    if (limitValueNumber === 0) {
      clearDeductibleSelection();
      return;
    }
    // Debounce ainda não alcançou o valor atual do LMI (ex.: campos
    // pré-preenchidos na edição): aguarda antes de revalidar a faixa,
    // senão a franquia pré-selecionada seria apagada indevidamente.
    if (debouncedLimitValueNumber !== limitValueNumber) return;
    // Opções ainda não carregadas (catálogo em fetch): não limpa uma franquia
    // pré-selecionada vinda da policy.
    if (deductibleOptions.length === 0) return;
    const stillVisible = visibleDeductibleOptions.some(
      option => String(option.value) === String(coverage.deductible)
    );
    if (!stillVisible) {
      clearDeductibleSelection();
    }
  }, [
    limitValueNumber,
    debouncedLimitValueNumber,
    coverage.deductible,
  ]);

  // Uma franquia só para o LMI informado: já vem marcada, não há o que escolher.
  // Só com o LMI preenchido (cobertura opcional sem valor não ganha franquia) e
  // depois que o debounce alcança o LMI digitado, para não marcar uma opção da
  // faixa anterior. Nunca mexe num card só de leitura.
  const singleDeductibleValue =
    isDisabled ||
    isDeductibleDisabled ||
    !isLmiFilled ||
    debouncedLimitValueNumber !== limitValueNumber
      ? undefined
      : resolveSingleDeductibleOption(
          displayDeductibleOptions,
          coverage.deductible
        );

  useEffect(() => {
    if (singleDeductibleValue) {
      coverage.onDeductibleChange?.(singleDeductibleValue);
    }
  }, [singleDeductibleValue]);

  return (
    <div
      className={`flex flex-col items-start gap-3 self-stretch rounded-xl p-4 sm:gap-2.5 ${
        isRequired
          ? 'bg-[rgba(231,232,233,0.5)] border border-alba-blue-500'
          : 'border border-alba-gray-400'
      }`}
    >
      <div className='flex w-full flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-1 flex-col items-start gap-1 self-stretch'>
          <Text className='text-sm font-bold text-alba-black-500'>
            {coverage.title}
          </Text>
          {coverage.description ? (
            <Text className='text-xs text-alba-gray-600 self-stretch'>
              {coverage.description}
            </Text>
          ) : null}
        </div>

        {isRequired && (
          <div className='flex flex-row items-center justify-center gap-2 rounded-xl bg-[rgba(44,40,100,0.2)] px-2 py-0.5 self-start sm:self-auto'>
            <Text className='text-xs font-bold text-alba-blue-500'>
              Obrigatória
            </Text>
          </div>
        )}
      </div>

      <div className='w-full h-0 border-t border-alba-gray-300' />

      <div className='flex w-full flex-col items-stretch gap-4 sm:flex-row sm:items-start'>
        <RequiredFieldTooltip active={isRequired} className='min-w-0 flex-1'>
        <div className='flex min-w-0 flex-1 flex-col items-start gap-2'>
          <div className='flex flex-row items-center gap-2.5'>
            <Text className='text-sm font-bold text-alba-black-500'>
              Limite Máximo de Indenização
            </Text>
            {isRequired && <span className='text-alba-error-red-500'>*</span>}
          </div>
          <Field.Root invalid={isLimitInvalid}>
            <Field.Input
              type='text'
              value={coverage.limitValue}
              onChange={e => {
                const rawDigits = e.target.value.replace(/\D/g, '');
                const numericValue = rawDigits ? parseInt(rawDigits, 10) : 0;
                coverage.onLimitChange?.(
                  numericValue > 0
                    ? formatCurrencyFromString(e.target.value)
                    : ''
                );
              }}
              placeholder='R$ 000,00'
              className='h-[39px] w-full px-4 py-3 text-xs sm:px-5'
              disabled={isDisabled}
            />
            {isLimitRequiredEmpty ? (
              <Field.ErrorText>Campo obrigatório</Field.ErrorText>
            ) : isLimitOutOfRange ? (
              <Field.ErrorText>
                Valor deve estar entre {getFormattedCurrency(min)} e{' '}
                {getFormattedCurrency(max)}
              </Field.ErrorText>
            ) : abovePercentCap ? (
              <Field.ErrorText>
                Valor máximo é{' '}
                {(
                  (coverage.percentOfBasicLimit ?? 0) * 100
                ).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
                % do LMI da cobertura básica (
                {getFormattedCurrency(percentCap ?? 0)})
              </Field.ErrorText>
            ) : null}
          </Field.Root>
        </div>
        </RequiredFieldTooltip>

        {hasDeductibleOptions && (
          <RequiredFieldTooltip
            active={isRequired || limitValueNumber > 0}
            flashKey={limitValueNumber > 0 ? 'filled' : 'empty'}
            className='min-w-0 flex-1'
          >
          <div className='flex min-w-0 flex-1 flex-col items-start gap-2'>
            <div className='flex flex-row items-center gap-1'>
              <Text className='text-sm font-bold text-alba-black-500'>
                Franquia
              </Text>
              {isRequired && <span className='text-alba-error-red-500'>*</span>}
            </div>
            <div className='flex flex-col gap-2 self-stretch sm:flex-row sm:items-stretch'>
              <Field.Root invalid={isDeductibleInvalid} className='flex-1'>
                <Select.Root
                  value={coverage.deductible ? [coverage.deductible] : []}
                  onValueChange={details =>
                    handleDeductibleSelect(details.value[0] || '')
                  }
                  positioning={{ sameWidth: true }}
                  deselectable
                  items={displayDeductibleOptions}
                  disabled={isDeductibleDisabled}
                >
                  <Select.Control>
                    <Select.Trigger
                      title={hasCustomDeductible ? customDeductibleLabel : undefined}
                      className='h-10 w-full overflow-hidden rounded-xl border border-alba-gray-400 px-3 sm:px-5'
                    >
                      {hasCustomDeductible ? (
                        <Text className='flex-1 min-w-0 truncate text-left text-xs text-alba-black-500'>
                          {customDeductibleLabel}
                        </Text>
                      ) : (
                        <Select.ValueText
                          placeholder={
                            isLmiFilled
                              ? 'Selecione a franquia'
                              : 'Preencha o LMI da cobertura'
                          }
                          className='text-xs text-alba-gray-600'
                        />
                      )}
                      <Select.Indicator />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {displayDeductibleOptions.map(option => (
                        <Select.Item key={option.value} item={option}>
                          <Select.ItemText>{option.label}</Select.ItemText>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
                {isDeductibleInvalid && (
                  <Field.ErrorText>Campo obrigatório</Field.ErrorText>
                )}
              </Field.Root>
              {coverage.allowCustomDeductible && (
                <button
                  type='button'
                  onClick={() => setIsCustomizeDialogOpen(true)}
                  disabled={isDeductibleDisabled}
                  aria-label='Personalizar franquia'
                  className='shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-alba-gray-200 text-alba-blue-500 hover:bg-alba-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <SquarePen className='w-5 h-5' strokeWidth={1.6} />
                </button>
              )}
            </div>
          </div>
          </RequiredFieldTooltip>
        )}
      </div>

      {showNoAvailableDeductibles && (
        <div className='flex items-start gap-2 self-stretch rounded-lg border border-red-200 bg-red-50 p-3'>
          <Text className='text-sm text-red-600'>
            O valor de LMI inserido não possui franquias disponíveis
          </Text>
        </div>
      )}

      {hasDeductibleOptions && coverage.allowCustomDeductible && (
        <CustomizeDeductibleDialog
          isOpen={isCustomizeDialogOpen}
          onOpenChange={setIsCustomizeDialogOpen}
          defaultLabel={dialogDefaultLabel}
          currentValue={customDeductible}
          onApply={handleApplyCustomDeductible}
        />
      )}

      {(hasBackendError || fireMaxExceeded) && (
        <div className='flex items-start p-3 gap-2 self-stretch rounded-lg bg-red-50 border border-red-200'>
          <Text className='text-sm text-red-600'>
            {fireMaxExceeded
              ? `Valor máximo para cobertura ${coverage.title} de ${getFormattedCurrency(max)} foi excedido.`
              : coverage.validationError}
          </Text>
        </div>
      )}

      {skipValueValidation && normalFlowWarning && (
        <div className='flex items-start gap-2 self-stretch rounded-lg border border-amber-200 bg-amber-50 p-3'>
          <Text className='text-sm text-amber-800'>{normalFlowWarning}</Text>
        </div>
      )}
    </div>
  );
}
